import * as Pinia from 'pinia';
import * as Vue from 'vue';

import {
  installAccessManagement as installChatAccessManagement,
  installLinkifyDirective,
} from '@nora-monorepo/nora-chat-ui';
import {
  bootstrapAxios,
  bootstrapConfig,
  bootstrapConnectivity,
  bootstrapKeycloak,
  bootstrapLogging,
  bootstrapPublicSettings,
  bootstrapSettings,
  bootstrapUserSettings,
  bootstrapWebviewMode,
  i18n,
  installFileDropDirective,
  isWebViewMode,
  persistedStorePlugin,
  useDarkModeStore,
  useGenericUserStore,
  useRoleStore,
  useWebViewStore,
  useWorkspaceStore,
} from '@nora-monorepo/ui-shared';

import AppRouter from '#nora-ui/AppRouter.vue';
import { installAccessManagement as installNoraAccessManagement } from '#nora-ui/bootstrap/directives/permission/permission-directive';
import { registerGlobalFunctions } from '#nora-ui/global';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { router } from '#nora-ui/router';
import { reportWebviewStatusToSimulator } from '#nora-ui/simulator/webview-status-reporter';
import { useBootstrapStore } from '#nora-ui/stores/bootstrap-store';
import { useConsentStore } from '#nora-ui/stores/consent-store';
import { useGroupStore } from '#nora-ui/stores/group-store';
import { useNoraSseStore } from '#nora-ui/stores/nora-sse-store';
import { useNotificationStore } from '#nora-ui/stores/notification-store';
import { useOperationStore } from '#nora-ui/stores/operation-store';
import { useSymbolStore } from '#nora-ui/stores/symbol-store';
import { useTracksStore } from '#nora-ui/stores/tracks-store';
import 'reflect-metadata';

import './assets/css/main.css';

const pinia = Pinia.createPinia();
pinia.use(persistedStorePlugin());

const app = Vue.createApp(AppRouter)
  .use(router)
  .use(i18n)
  .use(installNoraAccessManagement)
  .use(installChatAccessManagement)
  .use(installLinkifyDirective, {})
  .use(installFileDropDirective)
  .use(pinia);

if (!isWebViewMode()) {
  // With this setup, only console.x will be usable in webview. All the fancy adapters are disabled.
  bootstrapLogging(app);
}

useDarkModeStore();

app.mount('#app');

registerGlobalFunctions();

reportWebviewStatusToSimulator();

// Bootstrap drives a single ready phase in useBootstrapStore; the UI waits on it, so nothing that
// fetches data mounts before we're done.
async function bootstrap() {
  const bootstrapStore = useBootstrapStore();
  bootstrapStore.begin();

  // Config and connectivity before anything makes a request.
  const workspaceStore = useWorkspaceStore();
  bootstrapConfig(workspaceStore.selectedWorkspaceUrl);
  bootstrapConnectivity();
  const inWebViewMode = await bootstrapWebviewMode();

  try {
    // Sequential: each step depends on the previous one.
    await bootstrapAxios(inWebViewMode);
    await bootstrapPublicSettings();
    await bootstrapKeycloak(inWebViewMode);
    await bootstrapSettings(workspaceStore.selectedWorkspaceUrl);
    await bootstrapUserSettings();

    // Everything past here needs a logged-in user.
    const genericUserStore = useGenericUserStore();
    await genericUserStore.loadUser();

    if (!genericUserStore.user) {
      console.error('No user found');
      bootstrapStore.markNoUser();

      return;
    }

    bootstrapStore.enterLoading();
    await loadCoreData();
  }
  catch (error) {
    console.error('Error during bootstrap:', error);
    bootstrapStore.fail(error);

    return;
  }
  finally {
    // Closes the bootstrap window on every exit path; afterwards requests follow connectivity.
    if (inWebViewMode) {
      useWebViewStore().bootstrapComplete();
    }
  }

  bootstrapStore.markReady();
  schedulePostBootstrapWork();
}

// First-render data plus the always-on background services (SSE, tracks).
async function loadCoreData(): Promise<void> {
  const groupStore = useGroupStore();
  const operationStore = useOperationStore();
  const notificationStore = useNotificationStore();
  const consentStore = useConsentStore();
  const noraSseStore = useNoraSseStore();
  const mapStore = useMapStore();
  const tracksStore = useTracksStore();
  const roleStore = useRoleStore();

  await noraSseStore.startListening();

  mapStore.bootstrap();
  tracksStore.initialize();

  // The app cannot render meaningfully without these, so a failure here fails the bootstrap.
  const requiredBootstrapSteps = [
    groupStore.bootstrap(),
    operationStore.bootstrap(),
  ];

  // A failure in these degrades a feature rather than the app, so it must not take the bootstrap down with it.
  // Think of webviews, for example, where we do not show the operation create modal and therefore do not need the local roles.
  const optionalBootstrapSteps = [
    notificationStore.bootstrap(),
    consentStore.bootstrap(),
    roleStore.bootstrap(),
  ];

  await Promise.all([
    ...requiredBootstrapSteps,
    ...optionalBootstrapSteps.map(async bootstrapStep => await tolerateBootstrapFailure(bootstrapStep)),
  ]);
}

// Awaits an optional bootstrap step, logging a failure instead of propagating it.
async function tolerateBootstrapFailure(bootstrapStep: Promise<void>): Promise<void> {
  try {
    await bootstrapStep;
  }
  catch (error) {
    console.error('Optional bootstrap step failed:', error);
  }
}

function schedulePostBootstrapWork(): void {
  let scheduleIdle: (callback: () => void) => void;

  if (typeof window.requestIdleCallback === 'function') {
    scheduleIdle = callback => window.requestIdleCallback(callback, { timeout: 5000 });
  }
  else {
    scheduleIdle = callback => window.setTimeout(callback, 5000);
  }

  scheduleIdle(() => {
    useSymbolStore().warmSymbolCache().catch((error) => {
      console.warn('Symbol cache warm-up failed', error);
    });
  });
}

// Skip bootstrap on routes that opt out (e.g. /simulator).
void router.isReady().then(() => {
  if (router.currentRoute.value.meta.skipBootstrap) {
    return;
  }

  bootstrap().catch((error) => {
    // Catches the pre-try config/connectivity phase too, otherwise a failure there leaves the
    // splash up forever.
    console.error('Error during bootstrap:', error);
    useBootstrapStore().fail(error);
  });
});
